"use client";

import { motion } from "framer-motion";
import { Facebook, Camera, ExternalLink, Users, Heart } from "lucide-react";
import { fadeInUp } from "@/utils/animations";
import { useLanguage } from "@/contexts/LanguageContext";

// Icon mapping for SocialCard component
const socialIconMap = {
  "Facebook": Facebook,
  "Camera": Camera,
};

const SocialCard = () => {
  const { t, getSocialLinks } = useLanguage();
  const socialLinks = getSocialLinks();

  return (
    <section className="py-12 md:py-16 bg-background theme-transition">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInUp}
          className="max-w-4xl mx-auto"
        >
          {/* Card Container */}
          <div className="bg-gradient-to-br from-card to-background rounded-2xl border border-border shadow-xl shadow-black/30 overflow-hidden theme-transition">
            {/* Card Header */}
            <div className="p-8 md:p-10 border-b border-border bg-gradient-to-r from-card to-muted">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="mb-6 md:mb-0">
                  <h3 className="font-inter font-bold text-2xl md:text-3xl text-foreground mb-2">
                    {t('social.title')}
                  </h3>
                  <p className="font-inter font-normal text-base text-foreground/60">
                    {t('social.description')}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center bg-primary/10 text-primary px-4 py-2 rounded-lg">
                    <Users className="w-4 h-4 mr-2" />
                    <span className="font-inter font-medium text-sm">
                      {t('social.contactAvailable')}
                    </span>
                  </div>
                  <div className="flex items-center bg-primary/10 text-primary px-4 py-2 rounded-lg">
                    <Heart className="w-4 h-4 mr-2" />
                    <span className="font-inter font-medium text-sm">
                      {t('social.portfolioReady')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links Grid */}
            <div className="p-8 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {socialLinks.map((link, index) => {
                  const Icon = socialIconMap[link.iconName as keyof typeof socialIconMap] || Camera;
                  return (
                    <motion.a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="group relative bg-background border border-border hover:border-primary/50 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 overflow-hidden theme-transition"
                      style={{ 
                        '--hover-color': link.color,
                      } as React.CSSProperties}
                    >
                      {/* Background Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[var(--hover-color)]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-card to-muted border border-border group-hover:border-[var(--hover-color)]/50 flex items-center justify-center mr-4 transition-colors duration-300 theme-transition">
                              <Icon className="w-6 h-6 text-foreground group-hover:text-[var(--hover-color)] transition-colors duration-300" />
                            </div>
                            <div>
                              <h4 className="font-inter font-bold text-lg text-foreground group-hover:text-[var(--hover-color)] transition-colors duration-300">
                                {link.name}
                              </h4>
                              <div className="flex items-center mt-1">
                                <span className="font-inter font-normal text-xs text-foreground/40 bg-card px-2 py-1 rounded theme-transition">
                                  {link.stats}
                                </span>
                              </div>
                            </div>
                          </div>
                          <ExternalLink className="w-5 h-5 text-foreground/40 group-hover:text-[var(--hover-color)] transition-colors duration-300" />
                        </div>
                        
                        <p className="font-inter font-normal text-sm text-foreground/60 mb-4">
                          {link.description}
                        </p>
                        
                        {/* Visit Button */}
                        <div className="flex items-center justify-between">
                          <span className="font-inter font-medium text-sm text-foreground/40">
                            {t('social.clickToVisit')}
                          </span>
                          <div className="w-2 h-2 rounded-full bg-[var(--hover-color)] group-hover:animate-pulse" />
                        </div>
                      </div>
                    </motion.a>
                  );
                })}
              </div>

              {/* Additional Info */}
              <div className="mt-8 pt-8 border-t border-border">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <p className="font-inter font-normal text-sm text-foreground/60 mb-4 md:mb-0">
                    {t('social.additionalInfo')}
                  </p>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-primary">
                      <div className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse" />
                      <span className="font-inter font-medium text-xs">
                        {t('social.availableForContact')}
                      </span>
                    </div>
                    <div className="h-4 w-px bg-border" />
                    <span className="font-inter font-medium text-xs text-foreground/40">
                      {t('social.connectOnSocial')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialCard;